import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { teams } from "../../utils/constants";
import API from "../../services/api";
import { getReportLastDay, getReportMonthTimestamps } from "./reportDateUtils";

function PreviousMonthRow({ label, data, displayedTeamMembers, bgColorClass }) {
  const calculateTotalSpent = () => {
    return data.reduce((sum, entry) => sum + (parseFloat(entry.spent) || 0), 0).toFixed(2);
  };

  const calculateNewBusiness = () => {
    return data.reduce((sum, entry) => entry.business === 1 ? sum + (parseFloat(entry.spent) || 0) : sum, 0).toFixed(2);
  };

  const calculateOldBusiness = () => {
    return data.reduce((sum, entry) => entry.business === 0 ? sum + (parseFloat(entry.spent) || 0) : sum, 0).toFixed(2);
  };

  const calculateEcpm = () => {
    const totalImpressions = data.reduce((sum, entry) => sum + (parseFloat(entry.impressions) || 0), 0);
    const totalSpent = data.reduce((sum, entry) => sum + (parseFloat(entry.spent) || 0), 0);
    if (!totalImpressions) return "-";
    const ecpm = (totalSpent / totalImpressions) * 1000;
    return (!isNaN(ecpm) && ecpm !== Infinity) ? parseFloat(ecpm).toFixed(2) : "-";
  };

  const calculateSpentByIds = (salesIds, targetBusiness) => {
    return data.reduce((sum, entry) => {
      if (salesIds.includes(entry.sales_id) && entry.business === targetBusiness) {
        return sum + (parseFloat(entry.spent) || 0);
      }
      return sum;
    }, 0).toFixed(2);
  };

  return (
    <tr className={bgColorClass || "previous-month-row"}>
      <td><strong>{label}</strong></td>
      {displayedTeamMembers?.map((item, index) => {
        const ids = item?.isClubbed ? item.sales_person_ids : [item.sales_person_id];
        const total = parseFloat(calculateSpentByIds(ids, 1)) + parseFloat(calculateSpentByIds(ids, 0));
        return <td key={`${item?.name}-${label}-${index}`} title={total.toFixed(2)}>{Math.round(total)}</td>;
      })}
      <td title={calculateTotalSpent()}>{Math.round(parseFloat(calculateTotalSpent()))}</td>
      <td title={calculateNewBusiness()}>{Math.round(parseFloat(calculateNewBusiness()))}</td>
      <td title={calculateOldBusiness()}>{Math.round(parseFloat(calculateOldBusiness()))}</td>
      <td title={calculateEcpm()}>{calculateEcpm()}</td>
    </tr>
  );
}

function TeamwiseReports(props) {
  const {year, month, newReports} = props
  const [team, SetTeam] = useState();
  const [teamData, setTeamData] = useState([]);
  const [previousMonths, setPreviousMonths] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [currentDay, setCurrentDay] = useState(getDaysInMonth(year, month-1))
  function getStartAndEndOfMonthTimestamps(year, month) {
    return getReportMonthTimestamps(year, month);
  }
  function getDaysInMonth(year, month) {
    return getReportLastDay(year, month);
  }
  const { startTimestamp, endTimestamp } = getStartAndEndOfMonthTimestamps(year, month);
  const getTimestamp = (times) => {
    const startDate = new Date(year, month - 1, 1); 
    // Convert to timestamps (in seconds)
    const timestampFirstDayOfMonth = Math.floor(startDate.getTime() / 1000);
    return timestampFirstDayOfMonth + (times * 86400)
  }
  function calculateSpentByIds(salesIds, targetBusiness, index) {
    let targetDate = getTimestamp(index)
    return teamData.reduce((sum, entry) => {
      if (
        salesIds.includes(entry.sales_id) &&
        entry.date === targetDate &&
        entry.business === targetBusiness
      ) {
        return sum + (parseFloat(entry.spent) || 0);
      }
      return sum;
    }, 0);
  }
  const FetchData = async () => {
    try {
      let res = await API({
        url: "/reports/teamwisereports",
        method: "POST",
        responseType: "json",
        data: {
          team,
          startTimestamp,
           endTimestamp,
           newReport:newReports
        },
      });
      if (res.code === 200) {
        if (res.data && Array.isArray(res.data.currentMonth)) {
          setTeamData(res.data.currentMonth);
          setPreviousMonths(Array.isArray(res.data.previousMonths) ? res.data.previousMonths : []);
        } else {
          setTeamData(Array.isArray(res.data) ? res.data : []);
          setPreviousMonths([]);
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  const FetchSalesPersons = async () => {
    try {
      let res = await API({
        url: "/reports/teamwisesales",
        method: "POST",
        responseType: "json",
        data: {
          team,
        },
      });
      if (res.code === 200) {
        setTeamMembers(res.data);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const CalculateTotal = (index)=>{
    let targetDate = getTimestamp(index)
    const totalSpent = teamData.reduce((sum, entry) => {
      if (entry.date === targetDate && entry.spent) {
        return sum + entry.spent;
      }
      return sum;
    }, 0);
  
    return parseFloat(totalSpent).toFixed(2);
  }

  const CalculateOldBusiness = (index)=>{
    let targetDate = getTimestamp(index)
    const totalSpent = teamData.reduce((sum, entry) => {
      if (entry.business === 0 && entry.date === targetDate && entry.spent) {
        return sum + entry.spent;
      }
      return sum;
    }, 0);
  
    return parseFloat(totalSpent).toFixed(2);
  }

  const CalculateNewBusiness = (index)=>{
    let targetDate = getTimestamp(index)
    const totalSpent = teamData.reduce((sum, entry) => {
      if (entry.business === 1 && entry.date === targetDate && entry.spent) {
        return sum + entry.spent;
      }
      return sum;
    }, 0);
  
    return parseFloat(totalSpent).toFixed(2);
  }

  const calculateEcpm = (index) =>{
    let targetDate = getTimestamp(index)
    const totalImpressions = teamData.reduce((sum, entry) => {
      if (entry.date === targetDate && entry.impressions) {
        return sum + entry.impressions;
      }
      return sum;
    }, 0);
    console.log(totalImpressions)
    let impresssions = totalImpressions;
    let total = CalculateTotal(index)
    let ecpm = (total/impresssions) * 1000
    return ((!isNaN(ecpm)&& ecpm !== Infinity) ? parseFloat(ecpm).toFixed(2) : "-")
  }
  const handleCurrentDay = ()=>{
    setCurrentDay(getDaysInMonth(year, month - 1));
  }

  function getMonthName(monthNumber) {
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
  
    // Check if the input is a valid month number
    if (monthNumber >= 1 && monthNumber <= 12) {
      return months[monthNumber - 1];
    }
  }
  function capitalizeFirstLetter(string) {
    return string?.charAt(0).toUpperCase() + string?.slice(1);
  }
  function getPrevMonthInfo(m, y, offset) {
    const d = new Date(y, m - 1 - offset, 1);
    return { month: d.getMonth() + 1, year: d.getFullYear() };
  }

  const displayedTeamMembers = useMemo(() => {
    if (!Array.isArray(teamMembers) || !teamMembers.length) return [];
    const otherMembers = teamMembers.filter((member) => member?.status === 0);
    const normalMembers = teamMembers.filter((member) => member?.status !== 0);
    if (!otherMembers.length) return teamMembers;
    return [
      ...normalMembers,
      {
        name: "Others",
        isClubbed: true,
        sales_person_ids: otherMembers.map((member) => member.sales_person_id),
      },
    ];
  }, [teamMembers]);

  function calculateSpentColumnTotal(salesIds, targetBusiness) {
    let total = 0;
    if (teamData.length) {
      teamData.map((item) => {
        if (salesIds.includes(item.sales_id) && item.business === targetBusiness) {
          total += parseFloat(item.spent);
        } else {
          total += 0;
        }
      });
      return total;
    } else {
      return 0;
    }
  }
  const CalculateSalesTeamTotal = () => {
    let sum = 0;
    if (teamData.length) {
      teamData.map((item) => {
        item.spent ? sum += parseFloat(item.spent) : sum += 0
      })
      return parseFloat(sum).toFixed(2);
    }
    else {
      return 0;
    }
  }
  const CalculateBusinessTotal = (type) => {
    if (teamData.length) {
      if (type === "new_business") {
        let sum = 0;
        teamData.map((item) => {
          if (item.business === 1 && item.spent) {
            sum += parseFloat(item.spent);
          }
        })
        return parseFloat(sum).toFixed(2);
      } else if (type === "old_business") {
        let sum = 0;
        teamData.map((item) => {
          if (item.business === 0 && item.spent) {
            sum += parseFloat(item.spent);
          }
        })
        return parseFloat(sum).toFixed(2);
      } else if (type === "ecpm_total") {
        let ecpms = [];
        let ecpmsSum = 0;
        for (let rowIndex = 0; rowIndex < currentDay; rowIndex++) {
          if (teamData.length) {
            let targetDate = getTimestamp(rowIndex)
            const totalImpressions = teamData.reduce((sum, entry) => {
              if (entry.date === targetDate && entry.impressions) {
                return sum + entry.impressions;
              }
              return sum;
            }, 0);
            let impresssions = totalImpressions;
            let total = CalculateTotal(rowIndex)
            let ecpm = (total / impresssions) * 1000;
            if ((!isNaN(ecpm) && ecpm !== Infinity)) {
              ecpms.push(ecpm);
            }
          }
        }
        if (ecpms.length > 0) {
          ecpms?.map((item) => {
            ecpmsSum += item;
          })
          return (parseFloat(ecpmsSum) / (ecpms.length)).toFixed(2)
        } else {
          return 0;
        }
      }
    }
  }

  useEffect(() => {
    if (team) {
      FetchData();
      handleCurrentDay()
    }
  }, [team, month, year, newReports]);

  useEffect(()=>{
    if(team)
    FetchSalesPersons()
  },[team])

  return (
    <>
    <h5>Select Team</h5>
      <div className="d-flex gap-3">
        <Select
          className="mb-3 w-44"
          options={teams}
          onChange={(e) => SetTeam(e.value)}
        />
        <h6>Slected Team: {capitalizeFirstLetter(team)}</h6>
      </div>
    <div className="report-image">
     {team ?  <div className="overflow-x-auto responsive-table">
        <table className="table table-bordered report-table" id="teamwise-report">
          <thead>
            <tr>
              <td colSpan={displayedTeamMembers.length + 5}>
              <h4>{`${capitalizeFirstLetter(team)} - ${getMonthName(month)}` }</h4>
              </td>
            </tr>
            <tr>
              <th>Date</th>
              {displayedTeamMembers?.map((item, index) => {
                return <th key={item?.sales_person_id || item?.name || index}>{item?.name}</th>;
              })}
              <th>Total</th>
              <th>New Business</th>
              <th>Old Business</th>
              <th>ECPM</th>
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: currentDay }, (_, index) => (
              <tr key={index}>
                <td>{`${index + 1}-${month}-${year}`}</td>
                {displayedTeamMembers?.map((item, index1) => {
                const ids = item?.isClubbed ? item.sales_person_ids : [item.sales_person_id];
                const rawVal = calculateSpentByIds(ids, 1, index) + calculateSpentByIds(ids, 0, index);
                return <td key={`${item?.name}-${index1}`} title={parseFloat(rawVal).toFixed(2)}>{Math.round(rawVal)}</td>;
              })}
                <td title={CalculateTotal(index)}>{Math.round(parseFloat(CalculateTotal(index)))}</td>
                <td title={CalculateNewBusiness(index)}>{Math.round(parseFloat(CalculateNewBusiness(index)))}</td>
                <td title={CalculateOldBusiness(index)}>{Math.round(parseFloat(CalculateOldBusiness(index)))}</td>
                <td title={calculateEcpm(index)}>{calculateEcpm(index)}</td>
              </tr>
            ))}
              <tr>
                <td>Total</td>
                {displayedTeamMembers?.map((item, index) => {
                  const ids = item?.isClubbed ? item.sales_person_ids : [item.sales_person_id];
                  const totalVal = parseFloat(calculateSpentColumnTotal(ids, 1) + calculateSpentColumnTotal(ids, 0));
                  return <td key={`${item?.name}-total-${index}`} title={totalVal.toFixed(2)}>{Math.round(totalVal)}</td>;
                })}
                <td title={CalculateSalesTeamTotal()}>{Math.round(parseFloat(CalculateSalesTeamTotal()))}</td>
                <td title={CalculateBusinessTotal("new_business")}>{Math.round(parseFloat(CalculateBusinessTotal("new_business")))}</td>
                <td title={CalculateBusinessTotal("old_business")}>{Math.round(parseFloat(CalculateBusinessTotal("old_business")))}</td>
                <td title={CalculateBusinessTotal("ecpm_total")}>{CalculateBusinessTotal("ecpm_total")}</td>
              </tr>
              {previousMonths.map((pm, idx) => (
                <PreviousMonthRow
                  key={`pm-${pm?.year}-${pm?.month}`}
                  label={`${getMonthName(pm?.month)}-${pm?.year}`}
                  data={Array.isArray(pm?.data) ? pm.data : []}
                  displayedTeamMembers={displayedTeamMembers}
                  bgColorClass={idx % 2 === 0 ? "bg-yellow-200" : "bg-yellow-300"}
                />
              ))}
          </tbody>
        </table>
      </div> : null}
    </div>
    </>
  );
}

export default TeamwiseReports;
